// apps/web/src/components/WizardShell.tsx — Phase 7 plan 07-06.
//
// Onboarding wizard layout shell (mobile-first 375 px per D-07-03 +
// Phase 1-6 baseline). Renders a header bar with the restaurant
// brand, a 4-step indicator, and an optional back button. The body
// slot is rendered below.
//
// Props: currentStep drives the step indicator highlight. `onBack`
// is rendered as a button at the bottom of the shell so the wizard
// steps can reuse the back-navigation pattern without each page
// rolling its own.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

export type WizardStepId = "address" | "capacity" | "hours" | "review";

const STEP_ORDER: readonly WizardStepId[] = [
  "address",
  "capacity",
  "hours",
  "review",
];

export interface WizardShellProps {
  currentStep: WizardStepId;
  onBack?: () => void;
  children: ReactNode;
}

export function WizardShell(props: WizardShellProps): JSX.Element {
  const { t } = useTranslation();
  const currentIndex = STEP_ORDER.indexOf(props.currentStep);
  const total = STEP_ORDER.length;
  return (
    <main
      className="min-h-screen bg-bg text-text font-body flex flex-col px-4 py-8"
      style={{ fontVariantNumeric: "tabular-nums" }}
      data-testid="wizard-shell"
    >
      <header className="mb-6">
        <p className="font-heading text-sm uppercase tracking-wider text-text/70 mb-1">
          {t("onboarding.brand")}
        </p>
        <ol
          className="flex gap-3 text-sm"
          aria-label={t("onboarding.progressAria", {
            current: currentIndex + 1,
            total,
          })}
          data-testid="wizard-step-indicator"
        >
          {STEP_ORDER.map((step, idx) => {
            const active = idx === currentIndex;
            return (
              <li
                key={step}
                className={
                  active
                    ? "font-heading font-semibold text-accent"
                    : "text-text/60"
                }
                aria-current={active ? "step" : undefined}
              >
                {idx + 1}. {t(`onboarding.step.${step}`)}
              </li>
            );
          })}
        </ol>
      </header>
      <section className="flex-1 flex flex-col gap-6" data-testid="wizard-body">
        {props.children}
      </section>
      <footer className="mt-6">
        {props.onBack ? (
          <button
            type="button"
            onClick={props.onBack}
            data-testid="wizard-back"
            className="inline-flex items-center gap-2 text-sm text-text/70 hover:text-text"
          >
            <ArrowLeft size={16} aria-hidden />
            {t("onboarding.back")}
          </button>
        ) : null}
      </footer>
    </main>
  );
}
